import { DatePipe } from '@angular/common';
import { SignatureMethod } from 'common';

/** DocuSign envelope status, as the provider reports it. */
export enum EnvelopeStatus {
  Unknown = 'unknown',
  Created = 'created',
  Sent = 'sent',
  Delivered = 'delivered',
  Completed = 'completed',
  Declined = 'declined',
  Voided = 'voided',
  Correct = 'correct',
}

export const EnvelopeStatusLabel = new Map<string, string>([
  [EnvelopeStatus.Unknown, 'Unknown'],
  [EnvelopeStatus.Created, 'Created'],
  [EnvelopeStatus.Sent, 'Sent'],
  [EnvelopeStatus.Delivered, 'Delivered'],
  [EnvelopeStatus.Completed, 'Completed'],
  [EnvelopeStatus.Declined, 'Declined'],
  [EnvelopeStatus.Voided, 'Voided'],
  [EnvelopeStatus.Correct, 'Correct'],
]);

/** DocuSign recipient status, as the provider reports it. */
export enum EnvelopeRecipientStatus {
  Unknown = 'unknown',
  Created = 'created',
  Sent = 'sent',
  Delivered = 'delivered',
  Completed = 'completed',
  Declined = 'declined',
  AutoResponded = 'autoresponded',
  AuthenticationFailed = 'authenticationfailed',
}

export const EnvelopeRecipientStatusLabel = new Map<string, string>([
  [EnvelopeRecipientStatus.Unknown, 'Unknown'],
  [EnvelopeRecipientStatus.Created, 'Created'],
  [EnvelopeRecipientStatus.Sent, 'Sent'],
  [EnvelopeRecipientStatus.Delivered, 'Delivered'],
  [EnvelopeRecipientStatus.Completed, 'Completed'],
  [EnvelopeRecipientStatus.Declined, 'Declined'],
  [EnvelopeRecipientStatus.AutoResponded, 'Auto-responded'],
  [EnvelopeRecipientStatus.AuthenticationFailed, 'Authentication failed'],
]);

export interface ApplicationEnvelopeData {
  applicationId: number;
  loanId?: number;
  envelopeId: number;
  docuSignEnvelopeId: string;
  docuSignEnvelopeUrl: string;
  signatureMethod?: SignatureMethod;
  status: EnvelopeStatus;
  statusChangedUtc: Date;
  sentOnUtc?: Date;
  completedOnUtc?: Date;
  expiresOnUtc?: Date;
  voidedReason?: string;
  executedDocumentId?: number;
  /** An MSA's executed PDF, which is an entity document rather than a loan document. */
  executedEntityDocumentId?: number;
  certificateRetrievedOnUtc?: Date;
  recipients: EnvelopeRecipientData[];
}

export interface SignatureMethodSwitchOption {
  target: SignatureMethod;
  label: string;
}

export interface EnvelopeRecipientData {
  recipientId: number;
  customerId: number;
  customerName: string;
  routingOrder: number;
  status: EnvelopeRecipientStatus;
  statusChangedUtc: Date;
  signedOnUtc?: Date;
  declinedReason?: string;
}

/** One owner's signing state in DocuSign's own words, with the signed date or the decline reason. */
export const formatSignerStatus = (recipient: EnvelopeRecipientData, datePipe: DatePipe): string => {
  const label = EnvelopeRecipientStatusLabel.get(recipient.status) ?? recipient.status;

  if (recipient.status === EnvelopeRecipientStatus.Completed && recipient.signedOnUtc)
    return `${label} ${datePipe.transform(recipient.signedOnUtc)}`;

  if (recipient.status === EnvelopeRecipientStatus.Declined && recipient.declinedReason)
    return `${label}: ${recipient.declinedReason}`;

  return label;
};
